'use client';

import { Button, ConfigProvider, DatePicker, Form } from 'antd';
import locale from 'antd/locale/es_ES';
import 'bootstrap-icons/font/bootstrap-icons.min.css';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import React, { useEffect, useRef, useState } from 'react';

import { AppColors } from '../../theme';
import styles from './date-range.style';

dayjs.locale('es');

const LabelPreset = (label: string) => (
  <ConfigProvider
    locale={locale}
    theme={{
      components: {
        Button: {
          paddingBlock: 16,
          paddingInline: 6,
          defaultColor: AppColors.VA.NEUTRALS._90_,
          defaultBg: AppColors.VA.NEUTRALS.WHITE,
          textHoverBg: AppColors.VA.PRIMARY._10_,
          textTextHoverColor: AppColors.VA.PRIMARY._50_
        }
      }
    }}>
    <Button className='body-small' color='default' block variant='text' style={styles.presetOption}>
      {label}
    </Button>
  </ConfigProvider>
);

/**
 * Un conjunto de rangos de fechas predefinidos para seleccionar fechas pasadas.
 */
const pastDatePresets = [
  {
    label: LabelPreset('Hoy'),
    value: [dayjs(), dayjs()] as [Dayjs, Dayjs]
  },
  {
    label: LabelPreset('Últimos 7 días'),
    value: [dayjs().subtract(7, 'day'), dayjs()] as [Dayjs, Dayjs]
  },
  {
    label: LabelPreset('Últimas 2 semanas'),
    value: [dayjs().subtract(14, 'day'), dayjs()] as [Dayjs, Dayjs]
  },
  {
    label: LabelPreset('Último mes'),
    value: [dayjs().subtract(1, 'month'), dayjs()] as [Dayjs, Dayjs]
  },
  // {
  //   label: LabelPreset('Últimos 3 meses'),
  //   value: [dayjs().subtract(3, 'month'), dayjs()] as [Dayjs, Dayjs]
  // },
  {
    label: LabelPreset('Rango de fechas'),
    value: () => [dayjs().subtract(7, 'day'), dayjs()] as [Dayjs, Dayjs]
  }
];

/**
 * Un conjunto de rangos de fechas predefinidos para seleccionar fechas futuras.
 */
const futureDatePresets = [
  {
    label: LabelPreset('Hoy'),
    value: [dayjs(), dayjs()] as [Dayjs, Dayjs]
  },
  {
    label: LabelPreset('Próximos 7 días'),
    value: [dayjs(), dayjs().add(7, 'day')] as [Dayjs, Dayjs]
  },
  {
    label: LabelPreset('Próximas 2 semanas'),
    value: [dayjs(), dayjs().add(14, 'day')] as [Dayjs, Dayjs]
  },
  {
    label: LabelPreset('Próximo mes'),
    value: [dayjs(), dayjs().add(1, 'month')] as [Dayjs, Dayjs]
  },
  // {
  //   label: LabelPreset('Próximos 3 meses'),
  //   value: [dayjs(), dayjs().add(3, 'month')] as [Dayjs, Dayjs]
  // },
  {
    label: LabelPreset('Rango de fechas'),
    value: () => [dayjs(), dayjs().add(7, 'day')] as [Dayjs, Dayjs]
  }
];

/**
 * Props para el componente DateRange.
 */
type DateRangeProps = {
  /**
   * La etiqueta que se mostrará encima del selector de fecha, utilizada con Form.Item.
   */
  label: string;
  /**
   * El nombre que se asociará con el Form.Item.
   */
  name: string;
  /**
   * El rango de fechas actualmente seleccionado.
   */
  value: [Dayjs | null, Dayjs | null] | null | undefined;
  /**
   * Una función de devolución de llamada que se ejecuta cuando cambia el rango de fechas.
   *
   * @param dates - Un array que contiene las fechas de inicio y fin como objetos Dayjs.
   *
   * @param dateStrings - Un array que contiene las fechas de inicio y fin como cadenas formateadas.
   */
  onChange: (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => void;
  /**
   * Estilos personalizados opcionales que se aplicarán al contenedor del componente.
   */
  style?: React.CSSProperties;
  /**
   * Un conjunto personalizado de preajustes para anular los predeterminados.
   */
  customPresets?: typeof pastDatePresets;
  /**
   * Texto de marcador de posición para las entradas de fecha de inicio y fin.
   */
  placeholder?: [string, string];
  /**
   * Evita que aparezca el teclado móvil al enfocar los inputs del rango.
   * Por defecto activado para mejorar la UX en dispositivos móviles.
   */
  inputReadOnly?: boolean;
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
};

const { RangePicker } = DatePicker;
const customArrowIcon = <i className='bi bi-chevron-down' style={{ fontSize: '16px' }}></i>;

/**
 * Un selector de rango de fechas personalizado que envuelve el RangePicker de Ant Design.
 * Incluye rangos de fechas predefinidos (preajustes) y está configurado para la configuración regional en español.
 * @param {DateRangeProps} props - Las props para el componente DateRange.
 * @returns {JSX.Element} El componente DateRange renderizado.
 */
const DateRange = ({
  label,
  name,
  value,
  onChange,
  style,
  customPresets,
  placeholder,
  inputReadOnly = true,
  placement
}: DateRangeProps) => {
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
  const resolvedPlacement = placement ?? (isMobile ? 'bottomRight' : 'bottomLeft');
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const closingRef = useRef(false);
  const lastCloseAtRef = useRef<number>(0);

  useEffect(() => {
    if (!isMobile || !mobileOpen) return;
    const dropdowns = document.querySelectorAll<HTMLElement>(
      '.ant-picker-dropdown.custom-date-range-picker-dropdown'
    );
    const dropdown = dropdowns[dropdowns.length - 1];
    if (!dropdown) return;

    const handler = (ev: MouseEvent) => {
      const wrapper = dropdown.querySelector('.ant-picker-range-wrapper');
      if (wrapper && !wrapper.contains(ev.target as Node)) {
        closeDropdown();
      }
    };
    const touchHandler = (ev: TouchEvent) => {
      const wrapper = dropdown.querySelector('.ant-picker-range-wrapper');
      if (wrapper && !wrapper.contains(ev.target as Node)) {
        closeDropdown();
      }
    };
    const escHandler = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setMobileOpen(false);
      }
    };
    dropdown.addEventListener('mousedown', handler);
    dropdown.addEventListener('touchstart', touchHandler, { passive: true });
    document.addEventListener('keydown', escHandler);

    return () => {
      dropdown.removeEventListener('mousedown', handler);
      dropdown.removeEventListener('touchstart', touchHandler);
      document.removeEventListener('keydown', escHandler);
    };
  }, [isMobile, mobileOpen]);
  const handleChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    onChange(dates as [Dayjs | null, Dayjs | null] | null, dateStrings);
    if (isMobile && Array.isArray(dates) && dates.filter(Boolean).length === 2) {
      closingRef.current = true;
      lastCloseAtRef.current = Date.now();
      setMobileOpen(false);
      (document.activeElement as HTMLElement | null)?.blur?.();
      setTimeout(() => {
        closingRef.current = false;
      }, 400);
    }
  };
  const closeDropdown = () => {
    closingRef.current = true;
    lastCloseAtRef.current = Date.now();
    const dropdowns = document.querySelectorAll<HTMLElement>(
      '.ant-picker-dropdown.custom-date-range-picker-dropdown'
    );
    dropdowns.forEach((dd) => {
      const inputs = dd.querySelectorAll<HTMLInputElement>('.ant-picker-input input');
      inputs.forEach((inp) => inp.blur());
    });
    setMobileOpen(false);
    (document.activeElement as HTMLElement | null)?.blur?.();
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
    setTimeout(() => {
      closingRef.current = false;
    }, 400);
  };
  return (
    <ConfigProvider
      locale={locale}
      theme={{
        components: {
          DatePicker: {
            cellActiveWithRangeBg: AppColors.VA.PRIMARY._10_,
            cellHoverBg: 'transparent',
            marginXS: 15,
            textHeight: 70,
            presetsWidth: 185,
            presetsMaxWidth: 185
          }
        }
      }}>
      {label ? (
        <Form.Item label={label} name={name} style={style}>
          <RangePicker
            size='large'
            presets={customPresets || pastDatePresets}
            allowClear
            value={value ?? undefined}
            placeholder={placeholder || ['DD/MM/AAAA', 'DD/MM/AAAA']}
            format='DD/MM/YYYY'
            onChange={handleChange}
            dropdownClassName='custom-date-range-picker-dropdown'
            suffixIcon={customArrowIcon}
            inputReadOnly={inputReadOnly}
            placement={resolvedPlacement}
            onOpenChange={(open) => {
              if (isMobile) {
                if (closingRef.current) return;
                if (open && Date.now() - lastCloseAtRef.current < 500) return;
                setMobileOpen(open);
              }
            }}
          />
        </Form.Item>
      ) : (
        <RangePicker
          size='large'
          presets={customPresets || pastDatePresets}
          allowClear
          value={value ?? undefined}
          placeholder={placeholder || ['DD/MM/AAAA', 'DD/MM/AAAA']}
          format='DD/MM/YYYY'
          onChange={handleChange}
          dropdownClassName='custom-date-range-picker-dropdown'
          suffixIcon={customArrowIcon}
          style={style}
          inputReadOnly={inputReadOnly}
          placement={resolvedPlacement}
          renderExtraFooter={() =>
            isMobile ? (
              <div className='date-range-footer'>
                <button type='button' className='date-range-accept-btn' onClick={closeDropdown}>
                  Aceptar
                </button>
              </div>
            ) : null
          }
          onOpenChange={(open) => {
            if (isMobile) {
              if (closingRef.current) return;
              if (open && Date.now() - lastCloseAtRef.current < 500) return;
              setMobileOpen(open);
            }
          }}
        />
      )}
    </ConfigProvider>
  );
};

export default DateRange;

export { pastDatePresets, futureDatePresets };
